import type { MessageActionResult } from '../../state/chatTypes';

/** Where a result link leads and the translation key its label reads. */
type ActionLinkTarget = {
  labelKey: string;
  getPath: (detailsUrl: string) => string | undefined;
};

/** A result link ready to render. */
export type ActionLink = {
  labelKey: string;
  path: string;
};

const ORGANIZATION_PATH_PATTERN = /^\/organizations\/[^/?#]+/;

const toDetailsPage = (detailsUrl: string): string => detailsUrl;

const toVehiclesList = (detailsUrl: string): string | undefined => {
  const organizationPath = ORGANIZATION_PATH_PATTERN.exec(detailsUrl)?.[0];
  return organizationPath === undefined
    ? undefined
    : `${organizationPath}/vehicles`;
};

/** Every page a result link can open, with the label it reads. */
export const ACTION_LINK_TARGETS = {
  driverDetails: {
    labelKey: 'chat.actionResult.link.viewDriverDetails',
    getPath: toDetailsPage,
  },
  vehiclesList: {
    labelKey: 'chat.actionResult.link.viewVehiclesList',
    getPath: toVehiclesList,
  },
  geofenceSettings: {
    labelKey: 'chat.actionResult.link.viewGeofenceSettings',
    getPath: toDetailsPage,
  },
  details: {
    labelKey: 'chat.actionResult.link.viewDetails',
    getPath: toDetailsPage,
  },
} as const satisfies Record<string, ActionLinkTarget>;

// The design links assign_driver to the vehicles list while the backend
// links the driver's page; pointing it at `vehiclesList` switches it.
const ACTION_LINKS = new Map<string, ActionLinkTarget>([
  ['assign_driver', ACTION_LINK_TARGETS.driverDetails],
  ['create_geofence', ACTION_LINK_TARGETS.geofenceSettings],
  ['update_geofence', ACTION_LINK_TARGETS.geofenceSettings],
  ['enable_geofence_notification', ACTION_LINK_TARGETS.geofenceSettings],
]);

const getTargetFromPath = (detailsUrl: string): ActionLinkTarget =>
  detailsUrl.includes('/geofences/')
    ? ACTION_LINK_TARGETS.geofenceSettings
    : ACTION_LINK_TARGETS.details;

/**
 * Picks the link a result shows. A known action name picks the page and
 * label; otherwise the details path does.
 * @param actionResult - The result the link belongs to.
 * @returns The link, or `undefined` when the result has no page to open.
 */
export const getActionLink = ({
  action,
  detailsUrl,
}: Pick<MessageActionResult, 'action' | 'detailsUrl'>):
  | ActionLink
  | undefined => {
  if (detailsUrl === undefined) {
    return undefined;
  }
  const target = ACTION_LINKS.get(action) ?? getTargetFromPath(detailsUrl);
  const path = target.getPath(detailsUrl);
  return path === undefined ? undefined : { labelKey: target.labelKey, path };
};
